/* ==================================================================
 * wc-sticky-header.js
 * "Headroom" sticky header for #header-group.
 *
 * The announcement bar + header slide out of view on scroll-down and
 * slide back in on scroll-up. The marquee bar (first row) is NOT
 * sticky — while the header is fixed it is clipped above the viewport,
 * so scrolling up reveals only the announcement bar + header.
 *
 * Asymmetric thresholds keep the return-to-top seamless:
 *   engage  fixed only after the whole header has scrolled away  (y > offset)
 *   release fixed once back inside the marquee zone              (y <= marqueeH)
 * In that zone the fixed bar and the natural header line up pixel
 * for pixel, so dropping back to the natural layout doesn't jump.
 *
 * State classes on #header-group:
 *   .wc-headroom           always on once initialised
 *   .wc-headroom--fixed    position:fixed
 *   .wc-headroom--pinned   revealed  (marquee clipped)  – scrolling up
 *   .wc-headroom--unpinned hidden    (translateY -100%)  – scrolling down
 * ================================================================== */
(function () {
  'use strict';

  var header = document.getElementById('header-group');
  if (!header) return;

  header.classList.add('wc-headroom');

  var DELTA = 6; // ignore sub-pixel / trackpad jitter
  var lastY = window.pageYOffset || 0;
  var offset = 0;   // natural height of the whole group
  var marqueeH = 0; // height of the (non-sticky) marquee row
  var ticking = false;
  var resizeTimer;

  function marqueeNode() {
    var m = header.querySelector('.section--marquee');
    return m ? m.closest('.shopify-section') || m : null;
  }

  function measure() {
    var wasFixed = header.classList.contains('wc-headroom--fixed');
    if (wasFixed) header.classList.remove('wc-headroom--fixed');

    offset = header.offsetHeight || 0;

    var m = marqueeNode();
    marqueeH = m ? Math.round(m.getBoundingClientRect().height) : 0;
    header.style.setProperty('--wc-marquee-h', marqueeH + 'px');

    if (wasFixed) header.classList.add('wc-headroom--fixed');
  }

  // Toggle classes without animating the transform for this frame:
  // used for every position swap (enter fixed / drop back to natural)
  // so there is never a half-played slide.
  function setInstant(fn) {
    header.style.transition = 'none';
    fn();
    void header.offsetHeight; // force reflow -> commit
    header.style.transition = '';
  }

  function toNatural() {
    setInstant(function () {
      header.classList.remove(
        'wc-headroom--fixed',
        'wc-headroom--pinned',
        'wc-headroom--unpinned'
      );
    });
  }

  function update() {
    ticking = false;
    var y = window.pageYOffset || 0;

    if (header.classList.contains('wc-headroom--fixed')) {
      // Back in the marquee zone: the fixed bar and the natural header
      // coincide here, so hand back to the normal layout with no jump.
      if (y <= marqueeH) {
        toNatural();
        lastY = y;
        return;
      }
      if (Math.abs(y - lastY) >= DELTA) {
        if (y > lastY) {
          header.classList.add('wc-headroom--unpinned');
          header.classList.remove('wc-headroom--pinned');
        } else {
          header.classList.add('wc-headroom--pinned');
          header.classList.remove('wc-headroom--unpinned');
        }
        lastY = y;
      }
      return;
    }

    // Not fixed yet: only arm once the whole header has scrolled away,
    // so switching to position:fixed is invisible (row already gone).
    if (y > offset) {
      var down = y > lastY;
      setInstant(function () {
        header.classList.add('wc-headroom--fixed');
        header.classList.add(down ? 'wc-headroom--unpinned' : 'wc-headroom--pinned');
      });
    }
    lastY = y;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }

  measure();

  // Loaded already scrolled down → start hidden so the first scroll-up
  // slides the header in cleanly.
  if ((window.pageYOffset || 0) > offset) {
    setInstant(function () {
      header.classList.add('wc-headroom--fixed', 'wc-headroom--unpinned');
    });
    lastY = window.pageYOffset || 0;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('load', measure);
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measure, 150);
  });
})();
